import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../component/Navbar/Navbar";
import DownloadOSIcon from "../../component/DownloadButton/DownloadOSIcon";

// Desktop login handoff scheme — must match the app's login handler. Same scheme
// (and env override) the /sign-in-successful page uses, so the app receives the
// auth tokens identically.
const APP_SCHEME =
  process.env.REACT_APP_DESKTOP_SCHEME || "zerotypingai://signin-success";

const SignupSuccess = () => {
  // The backend redirects the desktop login here with the auth tokens in the
  // query string. Forward them to the native app via its custom URL scheme so
  // it becomes logged in.
  const deepLink = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const refresh = params.get("refresh");
    const picture = params.get("picture");
    if (!token) return "";
    const out = new URLSearchParams({ token, refresh: refresh || "" });
    if (picture) out.set("picture", picture);
    return `${APP_SCHEME}?${out.toString()}`;
  }, []);

  // Auto-open the app shortly after load so the "Open ZeroTypingAI?" dialog
  // appears without the user having to click.
  useEffect(() => {
    if (!deepLink) return;
    const t = setTimeout(() => {
      window.location.href = deepLink;
    }, 1200);
    return () => clearTimeout(t);
  }, [deepLink]);

  const handleReturnToApp = () => {
    // Falls back to the bare scheme if tokens are missing (page opened directly).
    window.location.href = deepLink || APP_SCHEME;
  };

  return (
    <div className="bg-white md:p-4 p-3 max-w-screen-2xl mx-auto min-h-screen flex flex-col">
      <Navbar />

      <div className="relative mt-6 md:mt-0 rounded-2xl overflow-hidden border border-[#3AB1F626] flex-1 flex flex-col">
        <div className="absolute inset-0 bg-[#D8EFFD] opacity-50 pointer-events-none" />

        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: "min(650px, 75vw)",
            height: "min(650px, 75vw)",
            left: "-15%",
            bottom: "-25%",
            background: "#3AB1F6",
            opacity: 0.65,
            filter: "blur(140px)",
            WebkitFilter: "blur(140px)",
          }}
        />
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: "min(650px, 75vw)",
            height: "min(650px, 75vw)",
            right: "-15%",
            bottom: "-25%",
            background: "#3AB1F6",
            opacity: 0.65,
            filter: "blur(140px)",
            WebkitFilter: "blur(140px)",
          }}
        />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-20">
          <h1 className="font-inter font-semibold text-black text-center text-[22px] sm:text-[28px] md:text-[34px] lg:text-[38px] leading-tight tracking-[-0.03em] mb-3 px-2">
            Click Below To Finish Login
          </h1>
          <p className="font-inter font-normal text-[#00000080] text-center text-[14px] sm:text-[16px] md:text-[18px] lg:text-[19.44px] leading-[150%] tracking-[-0.03em] max-w-[584px] mb-6 sm:mb-8 px-2">
            Press the below button to successfully log back into Zero Typing AI.
          </p>

          <button
            type="button"
            onClick={handleReturnToApp}
            style={{
              background: "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
              border: "1px solid #9797970A",
            }}
            className="hover:opacity-90 transition-opacity duration-300 ease-in-out text-white rounded-full flex items-center w-[200px] sm:w-[232px] h-[52px] sm:h-[60px] pl-[7px] sm:pl-[9px] pr-[28px] sm:pr-[41px] py-[7px] sm:py-[9px] gap-[16px] sm:gap-[22px] shadow-md"
          >
            <span className="bg-white text-[#3AB1F6] rounded-full w-[38px] h-[38px] sm:w-[42px] sm:h-[42px] flex items-center justify-center shrink-0">
              <DownloadOSIcon className="w-[18px] h-[18px]" />
            </span>
            <span className="font-inter font-semibold text-[14px] sm:text-[16px] whitespace-nowrap">
              Return to App
            </span>
          </button>

          <p className="font-inter text-[#00000080] text-center text-[13px] sm:text-[14px] mt-5 sm:mt-6">
            Don't have the app installed?{" "}
            <Link
              to="/windows"
              className="text-[#3AB1F6] font-medium hover:underline"
            >
              Download here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupSuccess;
